import React from "react";
import { Link } from "react-router-dom";
import { Smartphone, Bell, KeyRound, Home, Building2, ArrowRight, Wrench, PoundSterling, FileText, CalendarCheck, Search, LineChart, Camera, MessageSquare, ShieldCheck, TrendingUp, RefreshCw, Lock, Check, Clock, Apple, Play } from "lucide-react";
import Seo from "../components/Seo";
import { useToast } from "../hooks/use-toast";
import { AppSignInMock } from "../components/AppSignInMock";

const ROLE_TAB = { tenants: "Tenant", landlords: "Landlord", buyers: "Buyer", vendors: "Vendor" };

// App store links — update these once the apps are published.
const APP_STORE_URL = "https://apps.apple.com/gb/app/anadear-estates/id6806854522";
const PLAY_STORE_URL = "";

const StoreButton = ({ variant, sub, name, icon: Icon, url, onUnavailable, testid }) => {
  const handle = (e) => { if (!url) { e.preventDefault(); onUnavailable(); } };
  return (
    <a href={url || "#"} onClick={handle} target={url ? "_blank" : undefined} rel="noreferrer" data-testid={testid}
      className={`inline-flex items-center gap-3 pl-4 pr-6 py-2.5 rounded-xl border transition-colors ${variant === "light" ? "bg-white text-[#1c1c1e] border-white hover:bg-white/90" : "bg-[#1c1c1e] text-white border-white/20 hover:border-[#b8935f]"}`}>
      <Icon size={26} className={variant === "light" ? "text-[#1c1c1e]" : "text-white"} fill="currentColor" strokeWidth={0} />
      <span className="text-left leading-tight">
        <span className="block text-[10px] uppercase tracking-wide opacity-70">{sub}</span>
        <span className="block text-[16px] font-500 font-display">{name}</span>
      </span>
    </a>
  );
};

const DownloadButtons = ({ variant = "dark", className = "" }) => {
  const { toast } = useToast();
  const notify = () => toast({ title: "Launching soon", description: "The Anadear app arrives shortly — sign in on the web in the meantime." });
  return (
    <div className={`flex flex-col sm:flex-row items-center gap-3 ${className}`} data-testid="app-download-buttons">
      <StoreButton variant={variant} sub="Download on the" name="App Store" icon={Apple} url={APP_STORE_URL} onUnavailable={notify} testid="download-app-store" />
      <StoreButton variant={variant} sub="Get it on" name="Google Play" icon={Play} url={PLAY_STORE_URL} onUnavailable={notify} testid="download-google-play" />
    </div>
  );
};

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/319e8668-75ff-4fc7-8a49-82ce93f1e392/images/f11b21ae33c44ae40a32848bc0e7b471965724f2cbdc38682104184cec354e50.jpeg";
const APP_SCREENS = [
  { key: "search", img: "https://customer-assets-lxgj4vgw.emergentagent.net/job_anadear-web-launch/artifacts/lh3g0bd8_e4e679d4-36bb-4368-8477-b167d63dda9e.webp", alt: "Search smarter — browse homes for sale or rent and get a free instant valuation" },
  { key: "valuation", img: "https://customer-assets-lxgj4vgw.emergentagent.net/job_anadear-web-launch/artifacts/6hw1vqgk_7290ed67-65f7-49bb-b45c-c1ae840a0bbe.webp", alt: "Know what your home is worth — instant estimate and book a valuation" },
  { key: "concierge", img: "https://customer-assets-lxgj4vgw.emergentagent.net/job_anadear-web-launch/artifacts/lmgznxfe_cc5ac267-53cf-4c66-ab7d-5caa29bb33d3.webp", alt: "Talk to your property concierge — get tailored property matches fast" },
];

const PILLARS = [
  { icon: RefreshCw, title: "Always in sync", text: "The app and our office share one live system. Book a viewing, send a message or upload a document and our team sees it instantly — and vice versa." },
  { icon: Bell, title: "Never miss a beat", text: "Real-time push notifications for new listings, viewing confirmations, offers, rent due dates, renewals and compliance deadlines." },
  { icon: Lock, title: "Secure by design", text: "Bank-grade encryption, your own private document vault, and role-based access so you only ever see what's yours." },
  { icon: Clock, title: "Everything in one place", text: "No more digging through emails. Documents, payments, messages, viewings and progress — all together, all the time." },
];

const ROLES = [
  {
    key: "tenants", label: "For Tenants", icon: KeyRound,
    lead: "Renting made effortless — from your first viewing to the day you move out.",
    benefits: [
      { icon: Wrench, text: "Report a maintenance issue with photos and track it live to completion" },
      { icon: PoundSterling, text: "Pay rent securely and see a full history of every payment" },
      { icon: FileText, text: "Open your tenancy agreement, inventory and certificates any time" },
      { icon: CalendarCheck, text: "Book viewings and manage move-in / move-out appointments" },
      { icon: Bell, text: "Get reminders for rent, renewals and inspections before they're due" },
      { icon: MessageSquare, text: "Message your property manager directly and get quick replies" },
    ],
  },
  {
    key: "landlords", label: "For Landlords", icon: Building2,
    lead: "Your whole portfolio, managed from your pocket with total transparency.",
    benefits: [
      { icon: LineChart, text: "Live dashboard of rent received, arrears and net income per property" },
      { icon: FileText, text: "Monthly owner statements delivered automatically — download any time" },
      { icon: ShieldCheck, text: "Automatic EPC, Gas Safety & EICR reminders so you stay compliant" },
      { icon: Wrench, text: "Approve maintenance works and follow contractor progress in real time" },
      { icon: TrendingUp, text: "See marketing performance and enquiries while a property is being let" },
      { icon: MessageSquare, text: "One tap to reach your dedicated property manager" },
    ],
  },
  {
    key: "buyers", label: "For Buyers", icon: Home,
    lead: "Find the right home first — and see your purchase through with confidence.",
    benefits: [
      { icon: Search, text: "Create saved searches and get instant alerts the moment a match lists" },
      { icon: CalendarCheck, text: "Book and reschedule viewings in a couple of taps" },
      { icon: TrendingUp, text: "Track your purchase step-by-step from offer to completion" },
      { icon: PoundSterling, text: "Built-in mortgage and stamp-duty calculators to plan your budget" },
      { icon: FileText, text: "Store your ID, mortgage-in-principle and searches in one secure place" },
      { icon: MessageSquare, text: "Message the team and get straight answers, fast" },
    ],
  },
  {
    key: "vendors", label: "For Vendors", icon: PoundSterling,
    lead: "Sell smarter with a live window into everything happening on your sale.",
    benefits: [
      { icon: CalendarCheck, text: "See every viewing scheduled and read honest buyer feedback" },
      { icon: TrendingUp, text: "Track offers and the full sale progression in real time" },
      { icon: LineChart, text: "Live marketing performance across Rightmove, Zoopla and social" },
      { icon: Camera, text: "All your professional photography and floorplans in one archive" },
      { icon: FileText, text: "Sign paperwork digitally and keep every document to hand" },
      { icon: Bell, text: "Instant alerts when a new offer or viewing request comes in" },
    ],
  },
];

const FEATURES = [
  { icon: Bell, label: "Instant push alerts" },
  { icon: MessageSquare, label: "In-app messaging" },
  { icon: PoundSterling, label: "Secure online payments" },
  { icon: FileText, label: "Private document vault" },
  { icon: CalendarCheck, label: "Viewings & calendar" },
  { icon: TrendingUp, label: "Live progress tracking" },
  { icon: Search, label: "Saved searches" },
  { icon: ShieldCheck, label: "Compliance reminders" },
];

const AppPage = () => (
  <div className="min-h-[70vh]" data-testid="app-page">
    <Seo title="The Anadear App — Property in Your Pocket" description="The Anadear Estates app keeps tenants, landlords, buyers and vendors in real-time sync with our team — payments, viewings, documents, alerts and live progress tracking, all in one secure place." path="/app" />

    {/* Hero */}
    <section className="relative bg-[#1c1c1e] py-28 md:py-36 overflow-hidden">
      <img src={HERO_BG} alt="The Anadear app shown in an elegant Bath interior" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-[#1c1c1e]" />
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 60% 55% at 50% 45%, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.15) 60%, rgba(0,0,0,0) 85%)" }} />
      <div className="relative max-w-[900px] mx-auto px-5 md:px-8 text-center [text-shadow:0_2px_20px_rgba(0,0,0,0.5)]">
        <p className="text-[12px] tracking-[0.28em] uppercase text-[#d9b98a] mb-4 flex items-center justify-center gap-2"><Smartphone size={15} /> The Anadear App</p>
        <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl font-300 leading-[1.08] mb-6">Your move, in the palm of your hand</h1>
        <p className="text-white/80 text-[16px] md:text-[18px] leading-relaxed max-w-[680px] mx-auto mb-9">
          Renting, letting, buying or selling — the Anadear app connects you to our team in real time.
          Everything you need to move, manage and stay informed, together in one beautifully simple place.
        </p>
        <Link to="/portal" data-testid="app-hero-cta" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#b8935f] text-white text-[15px] font-500 hover:bg-[#a67f4d] transition-colors">Open your portal <ArrowRight size={17} /></Link>
        <DownloadButtons variant="light" className="justify-center mt-7" />
      </div>
    </section>

    {/* Pillars */}
    <section className="py-20 bg-[#faf7f2]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PILLARS.map((p) => (
            <div key={p.title} className="bg-white rounded-2xl border border-[#f0ece5] p-6">
              <div className="w-12 h-12 rounded-xl bg-[#b8935f]/10 flex items-center justify-center mb-4"><p.icon size={22} className="text-[#b8935f]" /></div>
              <h3 className="font-display text-[19px] font-500 text-[#2b2b2b] mb-2">{p.title}</h3>
              <p className="text-[14px] text-[#6a6a6a] leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* App showcase — real screens */}
    <section className="py-20 bg-white" data-testid="app-portals">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8">
        <div className="text-center max-w-[680px] mx-auto mb-12">
          <p className="text-[12px] tracking-[0.28em] uppercase text-[#b8935f] mb-3">See it in action</p>
          <h2 className="font-display text-[30px] md:text-[42px] font-300 text-[#2b2b2b] mb-4">A closer look at the app</h2>
          <p className="text-[15px] text-[#6a6a6a] leading-relaxed">One tailored sign-in for landlords, vendors, tenants and buyers — plus smart search, instant valuations, market insights and an AI property concierge.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 items-start max-w-[920px] mx-auto" data-testid="app-screens-grid">
          {APP_SCREENS.map((s) => (
            <div key={s.key} data-testid={`app-screen-${s.key}`} className="rounded-2xl overflow-hidden border border-[#f0ece5] bg-[#faf7f2] shadow-[0_10px_40px_rgba(0,0,0,0.05)] transition-transform duration-300 hover:-translate-y-1">
              <img src={s.img} alt={s.alt} className="w-full h-auto block" loading="lazy" />
            </div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link to="/login?role=tenants" data-testid="app-portals-cta" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#1c1c1e] text-white text-[14px] font-500 hover:bg-black transition-colors">Sign in to your portal <ArrowRight size={16} /></Link>
        </div>
      </div>
    </section>

    {/* Role deep-dives */}
    <section className="py-8 bg-white">
      <div className="max-w-[1100px] mx-auto px-5 md:px-8">
        {ROLES.map((r, i) => (
          <div key={r.key} data-testid={`app-role-${r.key}`} className={`py-14 border-b border-[#f0ece5] last:border-0 flex flex-col gap-10 lg:items-start ${i % 2 ? "lg:flex-row-reverse" : "lg:flex-row"}`}>
            <div className="w-full lg:w-[260px] lg:shrink-0" data-testid={`app-role-signin-img-${r.key}`}>
              <AppSignInMock active={ROLE_TAB[r.key]} testid={`app-role-signin-mock-${r.key}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="w-14 h-14 rounded-2xl bg-[#1c1c1e] flex items-center justify-center mb-5"><r.icon size={24} className="text-[#d9b98a]" /></div>
              <h2 className="font-display text-[30px] md:text-[36px] font-300 text-[#2b2b2b] leading-tight">{r.label}</h2>
              <p className="text-[15px] text-[#6a6a6a] leading-relaxed mt-3">{r.lead}</p>
              <Link to={`/login?role=${r.key}`} data-testid={`app-role-signin-${r.key}`} className="inline-flex items-center gap-2 mt-5 px-6 py-3 rounded-full bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] transition-colors">Sign in <ArrowRight size={15} /></Link>
              <div className="grid sm:grid-cols-2 gap-4 mt-8">
                {r.benefits.map((b, j) => (
                  <div key={j} className="flex items-start gap-3 p-4 rounded-xl bg-[#faf7f2] border border-[#f0ece5]">
                    <div className="w-9 h-9 rounded-lg bg-white border border-[#f0ece5] flex items-center justify-center shrink-0"><b.icon size={17} className="text-[#b8935f]" /></div>
                    <span className="text-[14px] text-[#4a4a4a] leading-relaxed">{b.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>

    {/* Feature strip */}
    <section className="py-20 bg-[#faf7f2]">
      <div className="max-w-[1100px] mx-auto px-5 md:px-8 text-center">
        <p className="text-[12px] tracking-[0.28em] uppercase text-[#b8935f] mb-3">Everything built in</p>
        <h2 className="font-display text-[30px] md:text-[40px] font-300 text-[#2b2b2b] mb-10">One app, every tool you need</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {FEATURES.map((f) => (
            <div key={f.label} className="bg-white rounded-xl border border-[#f0ece5] p-5 flex flex-col items-center gap-3">
              <f.icon size={22} className="text-[#b8935f]" />
              <span className="text-[13px] text-[#4a4a4a] font-500">{f.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Trust + CTA */}
    <section className="py-24 bg-[#1c1c1e]">
      <div className="max-w-[820px] mx-auto px-5 md:px-8 text-center">
        <ShieldCheck size={28} className="text-[#d9b98a] mx-auto mb-5" />
        <h2 className="font-display text-white text-[30px] md:text-[42px] font-300 leading-tight mb-5">Secure, private and free for every Anadear client</h2>
        <p className="text-white/60 text-[15px] leading-relaxed max-w-[600px] mx-auto mb-8">
          Your data is encrypted and only ever shared with the people who need it. There's nothing to pay —
          the app comes as standard the moment you work with us.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/portal" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#b8935f] text-white text-[15px] font-500 hover:bg-[#a67f4d] transition-colors">Open your portal <ArrowRight size={17} /></Link>
          <Link to="/services#valuation" className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/25 text-white text-[15px] font-500 hover:bg-white/10 transition-colors"><Check size={17} /> Book a free valuation</Link>
        </div>
        <DownloadButtons variant="light" className="justify-center mt-8" />
      </div>
    </section>
  </div>
);

export default AppPage;
